import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { Users, UserPlus, KeyRound, ShieldCheck, ShieldOff, Loader2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = {
  super_admin: "Super admin",
  admin: "Admin",
  compliance: "Compliance",
  sales: "Sales",
};

// Stable order for the role dropdown — least-privileged last so the
// natural reading order signals "scope shrinks downward."
const ROLE_OPTIONS = ["super_admin", "admin", "compliance", "sales"];

function RolePill({ role }) {
  const tone = {
    super_admin: "bg-gradient-to-br from-[#00E5FF]/20 to-[#0055FF]/20 text-[#00E5FF] border-[#00E5FF]/30",
    admin: "bg-white/[0.05] text-white border-white/10",
    compliance: "bg-amber-500/[0.10] text-amber-300 border-amber-400/30",
    sales: "bg-emerald-500/[0.10] text-emerald-300 border-emerald-400/30",
  }[role] || "bg-white/[0.04] text-zinc-400 border-white/10";
  return (
    <span
      data-testid={`team-row-role-pill-${role}`}
      className={`inline-flex items-center text-[10.5px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border ${tone}`}
    >
      {ROLE_LABELS[role] || role}
    </span>
  );
}

function StatusPill({ status }) {
  const active = status === "active";
  return (
    <span
      className={`inline-flex items-center text-[10.5px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border ${
        active ? "bg-emerald-500/[0.10] text-emerald-300 border-emerald-400/30"
               : "bg-zinc-500/[0.10] text-zinc-400 border-zinc-500/30"
      }`}
    >
      {active ? "Active" : "Deactivated"}
    </span>
  );
}

function InviteModal({ open, onClose, onInvited }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("sales");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/admin/team/invite`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), role }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `${r.status}`);
      toast.success(`Invited ${data.email} as ${ROLE_LABELS[data.role]}`);
      onInvited(data);
      setEmail("");
      setRole("sales");
      onClose();
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Invite failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <form onSubmit={submit} className="w-full max-w-md mx-4 rounded-2xl border border-white/[0.08] bg-[#0a0a0a] p-6" data-testid="team-invite-modal">
        <div className="flex items-center gap-2 mb-1">
          <UserPlus className="h-4 w-4 text-[#00E5FF]" />
          <h2 className="text-[14px] font-medium text-white">Invite team member</h2>
        </div>
        <p className="text-[12px] text-zinc-500 mb-5">
          Their first Google sign-in will land them in the role you choose.
        </p>
        <label className="block text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Email</label>
        <input
          type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          data-testid="team-invite-email"
          className="w-full rounded-xl border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[13px] text-white placeholder-zinc-600 mb-4 focus:outline-none focus:border-[#00E5FF]/40"
          placeholder="alice@neptunepay.io"
          autoFocus
        />
        <label className="block text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Role</label>
        <select
          value={role} onChange={(e) => setRole(e.target.value)}
          data-testid="team-invite-role"
          className="w-full rounded-xl border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[13px] text-white mb-5 focus:outline-none focus:border-[#00E5FF]/40"
        >
          {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
        </select>
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="rounded-xl px-4 py-2 text-[12.5px] text-zinc-400 hover:text-white">Cancel</button>
          <button type="submit" disabled={submitting} data-testid="team-invite-submit"
            className="rounded-xl px-4 py-2 text-[12.5px] bg-white text-black hover:bg-zinc-200 disabled:opacity-50 inline-flex items-center gap-1.5">
            {submitting && <Loader2 className="h-3 w-3 animate-spin" />} Invite
          </button>
        </div>
      </form>
    </div>
  );
}

function BuyPwdModal({ open, onClose, target, onSet }) {
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd !== confirm) { toast.error("Passwords don't match"); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/admin/team/${target.user_id}/buy-pwd`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ new_password: pwd }),
      });
      const data = await r.json();
      if (!r.ok) {
        const msg = Array.isArray(data?.detail)
          ? data.detail.map((d) => d.msg || d).join("; ")
          : data?.detail || `${r.status}`;
        throw new Error(msg);
      }
      toast.success(`Reveal password set for ${target.email}`);
      onSet();
      setPwd(""); setConfirm("");
      onClose();
    } catch (err) {
      toast.error(String(err.message));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !target) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <form onSubmit={submit} className="w-full max-w-md mx-4 rounded-2xl border border-white/[0.08] bg-[#0a0a0a] p-6" data-testid="team-buy-pwd-modal">
        <div className="flex items-center gap-2 mb-1">
          <KeyRound className="h-4 w-4 text-amber-400" />
          <h2 className="text-[14px] font-medium text-white">Set buy-rate reveal password</h2>
        </div>
        <p className="text-[12px] text-zinc-500 mb-1">For <span className="text-white">{target.email}</span></p>
        <p className="text-[11px] text-zinc-600 mb-5">
          Min 12 chars, must include digit + symbol. Share it with them out-of-band.
        </p>
        <label className="block text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">New password</label>
        <input
          type="password" required value={pwd} onChange={(e) => setPwd(e.target.value)}
          data-testid="team-buy-pwd-input"
          className="w-full rounded-xl border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[13px] text-white mb-4 focus:outline-none focus:border-amber-400/40"
          autoFocus
        />
        <label className="block text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Confirm</label>
        <input
          type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
          data-testid="team-buy-pwd-confirm"
          className="w-full rounded-xl border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[13px] text-white mb-5 focus:outline-none focus:border-amber-400/40"
        />
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="rounded-xl px-4 py-2 text-[12.5px] text-zinc-400 hover:text-white">Cancel</button>
          <button type="submit" disabled={submitting} data-testid="team-buy-pwd-submit"
            className="rounded-xl px-4 py-2 text-[12.5px] bg-amber-400 text-black hover:bg-amber-300 disabled:opacity-50 inline-flex items-center gap-1.5">
            {submitting && <Loader2 className="h-3 w-3 animate-spin" />} Set password
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AdminTeam() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [buyPwdTarget, setBuyPwdTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/team`, { credentials: "include" });
      if (!r.ok) throw new Error(`${r.status}`);
      const data = await r.json();
      setRows(data.members || []);
    } catch (err) {
      toast.error(`Failed to load team: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (user && user.role !== "super_admin") {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const changeRole = async (row, newRole) => {
    if (newRole === row.role) return;
    try {
      const r = await fetch(`${API}/admin/team/${row.user_id}/role`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role: newRole }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `${r.status}`);
      toast.success(`${row.email} → ${ROLE_LABELS[newRole]}`);
      load();
    } catch (err) {
      toast.error(String(err.message));
      load(); // reset dropdown to canonical state
    }
  };

  const toggleStatus = async (row) => {
    const action = row.status === "active" ? "deactivate" : "reactivate";
    try {
      const r = await fetch(`${API}/admin/team/${row.user_id}/${action}`, {
        method: "POST", credentials: "include",
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `${r.status}`);
      toast.success(`${row.email} ${action}d`);
      load();
    } catch (err) {
      toast.error(String(err.message));
    }
  };

  return (
    <div className="p-8 max-w-6xl" data-testid="admin-team-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-600 mb-1">— Workspace</div>
          <h1 className="text-[28px] font-medium text-white inline-flex items-center gap-2.5">
            <Users className="h-5 w-5 text-[#00E5FF]" /> Team
          </h1>
          <p className="text-[13px] text-zinc-500 mt-1">
            Invite, promote, demote, and (de)activate Neptune back-office members.
          </p>
        </div>
        <button
          onClick={() => setInviteOpen(true)}
          data-testid="team-invite-open-btn"
          className="rounded-xl px-4 py-2 text-[12.5px] bg-white text-black hover:bg-zinc-200 inline-flex items-center gap-1.5"
        >
          <UserPlus className="h-3.5 w-3.5" /> Invite member
        </button>
      </div>

      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.015] overflow-hidden">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 bg-white/[0.02]">
              <th className="px-5 py-3 font-normal">Email</th>
              <th className="px-3 py-3 font-normal">Role</th>
              <th className="px-3 py-3 font-normal">Status</th>
              <th className="px-3 py-3 font-normal">Reveal pwd</th>
              <th className="px-3 py-3 font-normal">Last login</th>
              <th className="px-3 py-3 font-normal text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="team-list-tbody">
            {loading && (
              <tr><td colSpan={6} className="px-5 py-6 text-zinc-500 text-center">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-6 text-zinc-500 text-center">No team members yet.</td></tr>
            )}
            {rows.map((row) => (
              <tr key={row.user_id} className="border-t border-white/[0.04] hover:bg-white/[0.02]" data-testid={`team-row-${row.user_id}`}>
                <td className="px-5 py-3 text-white">
                  <div className="flex flex-col">
                    <span>{row.email}</span>
                    {row.name && <span className="text-[11px] text-zinc-500">{row.name}</span>}
                  </div>
                </td>
                <td className="px-3 py-3">
                  <select
                    value={row.role}
                    onChange={(e) => changeRole(row, e.target.value)}
                    disabled={row.user_id === user?.user_id}
                    data-testid={`team-row-role-select-${row.user_id}`}
                    className="rounded-lg border border-white/[0.08] bg-white/[0.02] px-2 py-1 text-[12px] text-white disabled:opacity-50"
                    title={row.user_id === user?.user_id ? "You can't change your own role" : ""}
                  >
                    {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                  </select>
                </td>
                <td className="px-3 py-3"><StatusPill status={row.status} /></td>
                <td className="px-3 py-3 text-zinc-400">
                  {row.has_buy_rate_password
                    ? <span className="text-emerald-300 text-[11.5px]">• Set</span>
                    : <span className="text-zinc-600 text-[11.5px]">— Not set</span>}
                </td>
                <td className="px-3 py-3 text-zinc-500 text-[11.5px]">
                  {row.last_login_at ? row.last_login_at.slice(0, 10) : "—"}
                </td>
                <td className="px-3 py-3 text-right whitespace-nowrap">
                  <button
                    onClick={() => setBuyPwdTarget(row)}
                    data-testid={`team-row-buy-pwd-btn-${row.user_id}`}
                    className="text-[11.5px] text-amber-300 hover:text-amber-200 inline-flex items-center gap-1 mr-3"
                  >
                    <KeyRound className="h-3 w-3" /> Set pwd
                  </button>
                  {row.user_id !== user?.user_id && (
                    <button
                      onClick={() => toggleStatus(row)}
                      data-testid={`team-row-toggle-status-btn-${row.user_id}`}
                      className={`text-[11.5px] inline-flex items-center gap-1 ${
                        row.status === "active" ? "text-zinc-400 hover:text-red-300" : "text-emerald-300 hover:text-emerald-200"
                      }`}
                    >
                      {row.status === "active"
                        ? <><ShieldOff className="h-3 w-3" /> Deactivate</>
                        : <><ShieldCheck className="h-3 w-3" /> Reactivate</>}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <InviteModal open={inviteOpen} onClose={() => setInviteOpen(false)} onInvited={() => load()} />
      <BuyPwdModal open={!!buyPwdTarget} target={buyPwdTarget} onClose={() => setBuyPwdTarget(null)} onSet={() => load()} />
    </div>
  );
}
